import React, { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import dayjs from "dayjs";
import { JOBS_LOG_ENDPOINT, JOB_EXECUTE_ENDPOINT } from '../../config/urls';
import { useAuth } from "../../hooks/AuthProvider";
import { useNavigate } from 'react-router-dom';
import AppTable from '../../common/app-table';
import { formatWithTzLabel } from '../../utils/dateUtils';

const columns = [
  { id: 'job', label: 'Cron Name', render: (j) => j.job?.name || "-" },
  { id: 'assigned_orders', label: 'Total Order Notifications' },
  { id: 'job_executed_at', label: 'Last Executed', render: (j) => formatWithTzLabel(j.job_executed_at, "MMMM D, YYYY, h:mm A") },
  { id: 'delivery_date', label: 'Delivery Date', render: (j) => j.delivery_date ? dayjs(j.delivery_date).format("DD/MM/YYYY") : "-" },
  { id: 'job_executed_by', label: 'Executed By', render: (j) => j.job_executed_by?.name || "-" },
];

function JobsTable({ searchInput, fromDate, toDate, executedBy, notificationCount, refreshKey }) {
  const [rowCount, setRowCount] = useState(0);
  const { getValidToken } = useAuth();
  const auth = useAuth();
  const navigate = useNavigate();

  const [jobsData, setJobsData] = useState([]);
  const [controller, setController] = useState({ page: 0, rowsPerPage: 20 });
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem('tenant');
        const searchURL = searchInput ? `&search=${encodeURIComponent(searchInput)}` : '';
        const fromDateURL = fromDate && dayjs.isDayjs(fromDate) && fromDate.isValid()
          ? `&from_date=${fromDate.format("YYYY-MM-DD")}`
          : '';
        const toDateURL = toDate && dayjs.isDayjs(toDate) && toDate.isValid()
          ? `&to_date=${toDate.format("YYYY-MM-DD")}`
          : '';
        const executedByURL = executedBy ? `&executed_by=${encodeURIComponent(executedBy)}` : '';
        const notificationCountURL = notificationCount ? `&notification_count=${notificationCount}` : '';
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-tenant-ID': tenant,
        };
        const response = await fetch(
          `${JOBS_LOG_ENDPOINT}?limit=${controller.rowsPerPage}&offset=${controller.page * controller.rowsPerPage}${searchURL}${fromDateURL}${toDateURL}${executedByURL}${notificationCountURL}`,
          { headers }
        );
        if (!response.ok) {
          const errorData = await response.json();
          if (errorData.code === 'user_inactive') { auth.logOut(); navigate('/login'); }
          throw new Error(errorData.detail || 'Failed to fetch data');
        }
        const jsonData = await response.json();
        setJobsData(Array.isArray(jsonData.results) ? jsonData.results : []);
        setRowCount(jsonData.count || 0);
      } catch (error) {
        console.error('Error fetching data:', error.message);
        setAlert({ open: true, message: error.message, severity: 'error' });
      }
    };
    fetchData();
  }, [controller, refreshKey, searchInput, fromDate, toDate, executedBy, notificationCount]);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', mt: 0.2, boxShadow: "none" }}>
      <Box sx={{ p: 2 }}>
        {alert.open && (
          <Alert onClose={() => setAlert((a) => ({ ...a, open: false }))} severity={alert.severity} sx={{ mb: 2 }}>
            {alert.message}
          </Alert>
        )}
      </Box>

      <AppTable
        columns={columns}
        rows={jobsData}
        rowKey={(j, i) => i}
        emptyMessage="No match found"
        maxHeight={600}
        count={rowCount}
        page={controller.page}
        rowsPerPage={controller.rowsPerPage}
        onPageChange={(e, newPage) => setController((prev) => ({ ...prev, page: newPage }))}
        onRowsPerPageChange={(e) => setController((prev) => ({ ...prev, rowsPerPage: parseInt(e.target.value, 10), page: 0 }))}
      />
    </Paper>
  );
}

export default JobsTable;